import { Button, Card, CircularProgress, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLazyGetTeamProjectsPageQuery } from '../../api/teams'
import type { IProjectsListItem } from '../../api/types'
import { CURSOR_LIST_PAGE_SIZE } from '../../consts/api'
import { routesProfile } from '../../consts'
import { ProjectCoverImage } from '../common/ProjectCoverImage'

type TeamProjectsSectionProps = {
  teamId: number
  total?: number
}

export const TeamProjectsSection = ({ teamId, total = 0 }: TeamProjectsSectionProps) => {
  const navigate = useNavigate()
  const [cursor, setCursor] = useState<string | null>(null)
  const [items, setItems] = useState<IProjectsListItem[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [trigger, { data, isLoading, isFetching, isError }] = useLazyGetTeamProjectsPageQuery()

  useEffect(() => {
    if (total <= 0) {
      setCursor(null)
      setItems([])
      setNextCursor(null)
      return
    }

    setCursor(null)
    setItems([])
    setNextCursor(null)
    trigger({ teamId, limit: CURSOR_LIST_PAGE_SIZE, cursor: null }, true)
  }, [teamId, total, trigger])

  useEffect(() => {
    if (cursor == null) {
      return
    }

    trigger({ teamId, limit: CURSOR_LIST_PAGE_SIZE, cursor }, true)
  }, [cursor, teamId, trigger])

  useEffect(() => {
    if (!data) {
      return
    }

    setNextCursor(data.meta?.next_cursor ?? null)
    setItems((prev) => (cursor ? [...prev, ...(data.items ?? [])] : (data.items ?? [])))
  }, [data, cursor])

  const canLoadMore = nextCursor != null && nextCursor !== '' && !isFetching

  const openProject = (project: IProjectsListItem) => {
    const slug = (project.slug ?? '').trim()
    if (slug) {
      navigate(`/${routesProfile.PROJECTS.path}/${slug}`)
    }
  }

  return (
    <Card variant="primaryAdaptiveCard">
      <Stack gap={1.5}>
        <Typography variant="h3">Участвует в проектах</Typography>

        {isLoading && (
          <Stack direction="row" gap={1.5} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body1">Загружаем проекты...</Typography>
          </Stack>
        )}

        {isError && (
          <Typography color="error">
            Не удалось загрузить проекты команды.
          </Typography>
        )}

        {!isLoading && !isError && items.length > 0 && (
          <Stack gap={1}>
            {items.map((project) => (
              <Stack
                key={project.id}
                direction="row"
                gap={1.25}
                alignItems="center"
                sx={{ cursor: 'pointer', minWidth: 0 }}
                onClick={() => openProject(project)}
              >
                <ProjectCoverImage
                  src={project.image}
                  alt={project.name}
                  sx={{ width: 36, height: 36, borderRadius: '8px', flexShrink: 0 }}
                />
                <Typography variant="body1" noWrap minWidth={0}>
                  {project.name}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}

        {!isLoading && !isError && items.length === 0 && (
          <Typography variant="body1">Команда пока не участвует в проектах.</Typography>
        )}

        {canLoadMore && (
          <Stack direction="row" justifyContent="flex-end">
            <Button variant="follow" disabled={isFetching} onClick={() => setCursor(nextCursor)}>
              {isFetching ? 'Загрузка...' : 'Показать ещё'}
            </Button>
          </Stack>
        )}
      </Stack>
    </Card>
  )
}
